import { supabase, isSupabaseConfigured } from './supabaseClient';

// ---- Mapowanie wierszy DB (snake_case) -> kształt używany w komponentach (camelCase) ----

const mapDog = (r) => ({
  id: r.id,
  name: r.name,
  fullName: r.full_name,
  birthDate: r.birth_date,
  category: r.category,
  origin: r.origin,
  profileImage: r.profile_image_url,
  pedigreeImage: r.pedigree_image_url,
  images: Array.isArray(r.images) ? r.images : [],
  description: { pl: r.description_pl || '', en: r.description_en || '' },
  sortOrder: r.sort_order,
  visible: r.visible !== false, // brak kolumny (przed migracją) -> traktuj jako widoczny
});

const mapLitter = (r) => ({
  id: r.id,
  letter: r.letter,
  date: r.date,
  presentationImage: r.presentation_image_url,
  gallery: Array.isArray(r.gallery) ? r.gallery : [],
  description: { pl: r.description_pl || '', en: r.description_en || '' },
  sortOrder: r.sort_order,
  visible: r.visible !== false,
});

// Wartość dwujęzyczna zapisana w kolumnie tekstowej jako JSON {pl,en}
// (wstecznie kompatybilne: zwykły tekst = tylko polski).
export function parseBi(v) {
  if (v == null) return { pl: '', en: '' };
  if (typeof v === 'object') return { pl: v.pl || '', en: v.en || '' };
  const s = String(v).trim();
  if (s.startsWith('{') && s.endsWith('}')) {
    try {
      const o = JSON.parse(s);
      if (o && (typeof o.pl === 'string' || typeof o.en === 'string')) return { pl: o.pl || '', en: o.en || '' };
    } catch { /* nie JSON */ }
  }
  return { pl: String(v), en: '' };
}

// Tekst w danym języku z fallbackiem na polski.
export const biText = (bi, lang) => (bi && (bi[lang] || bi.pl)) || '';

const mapShow = (r) => ({
  id: r.id,
  dogName: r.dog_name,
  date: r.date,
  location: r.location,
  type: parseBi(r.type),
  achievement: parseBi(r.achievement),
  judge: r.judge,
  visible: r.visible !== false,
});

// Ukryte = visible === false. Filtrowanie po stronie klienta (odporne na brak kolumny).
const onlyVisible = (rows) => rows.filter((x) => x.visible !== false);

// ---- Odczyt (strona publiczna) ----

export async function fetchDogs({ includeHidden = false } = {}) {
  if (!isSupabaseConfigured) return [];
  const { data, error } = await supabase
    .from('dogs')
    .select('*')
    .order('sort_order', { ascending: true })
    .order('created_at', { ascending: true });
  if (error) throw error;
  const rows = data.map(mapDog);
  return includeHidden ? rows : onlyVisible(rows);
}

export async function fetchDogById(id) {
  if (!isSupabaseConfigured) return null;
  const { data, error } = await supabase.from('dogs').select('*').eq('id', id).maybeSingle();
  if (error) throw error;
  return data ? mapDog(data) : null;
}

export async function fetchLitters({ includeHidden = false } = {}) {
  if (!isSupabaseConfigured) return [];
  const { data, error } = await supabase
    .from('litters')
    .select('*')
    .order('sort_order', { ascending: true })
    .order('created_at', { ascending: true });
  if (error) throw error;
  const rows = data.map(mapLitter);
  return includeHidden ? rows : onlyVisible(rows);
}

export async function fetchLitterById(id) {
  if (!isSupabaseConfigured) return null;
  const { data, error } = await supabase.from('litters').select('*').eq('id', id).maybeSingle();
  if (error) throw error;
  return data ? mapLitter(data) : null;
}

export async function fetchShows({ includeHidden = false } = {}) {
  if (!isSupabaseConfigured) return [];
  const { data, error } = await supabase
    .from('shows')
    .select('*')
    .order('date', { ascending: false });
  if (error) throw error;
  const rows = data.map(mapShow);
  return includeHidden ? rows : onlyVisible(rows);
}

export async function fetchGallery({ includeHidden = false } = {}) {
  if (!isSupabaseConfigured) return [];
  const { data, error } = await supabase
    .from('gallery')
    .select('*')
    .order('sort_order', { ascending: true })
    .order('created_at', { ascending: true });
  if (error) throw error;
  const rows = data.map((r) => ({ id: r.id, url: r.image_url, caption: r.caption, visible: r.visible !== false }));
  return includeHidden ? rows : onlyVisible(rows);
}

export async function fetchContentMap() {
  if (!isSupabaseConfigured) return {};
  const { data, error } = await supabase.from('site_content').select('*');
  if (error) throw error;
  const map = {};
  for (const r of data) map[r.key] = { pl: r.value_pl || '', en: r.value_en || '' };
  return map;
}
